import { useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';

import { Button } from '@components/button';
import { CancelSaveButtons } from '@components/cancel-save-buttons';
import { Card } from '@components/card';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconEdit } from '@components/icons/edit';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconRedo } from '@components/icons/redo';
import { IconRemove } from '@components/icons/remove';
import { IconTeam } from '@components/icons/team';
import { Spinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { Select } from '@components/select';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Tag } from '@components/tag';
import { Text } from '@components/text';
import { useToaster } from '@components/toaster';

import { useModal } from 'features/modals';
import { useStore } from 'models/Provider';

import {
  taktikalGetToken,
  taktikalGetCosigners,
  taktikalRemoveCosigner,
  useTaktikalSignatureLocation,
  SignatureLocationOptions,
  useUpdateTaktikalSignatureLocation,
} from './api';

type TaktikalHeadProps = {
  isConnected: boolean;
};
const TaktikalHeader = ({ isConnected }: TaktikalHeadProps) => {
  const { t } = useStore();
  const { showModal } = useModal();
  return (
    <>
      <Stack space={1} orientation="horizontal" justify={'space-between'}>
        <Stack space={2}>
          <Text font="heading2" text={t('company.settings.apps.taktikal')} />
          <Stack space={0} orientation="horizontal">
            {isConnected ? (
              <Tag
                variant="green"
                size="small"
                text={t('integrations.taktikal.settings.header.connected')}
              />
            ) : (
              <Tag
                variant="purple"
                size="small"
                text={t('integrations.taktikal.settings.header.notconnected')}
              />
            )}
          </Stack>
        </Stack>
        {isConnected ? (
          <Actions>
            <Menu buttonProps={{ variant: 'ghost', iconLeft: <IconMoreVertical /> }}>
              <Menu.Item
                label={t('integrations.taktikal.settings.cosigners.reconnect')}
                icon={<IconRedo />}
                onSelect={() => {
                  showModal('INTEGRATION_TAKTIKAL_CONNECT', { isEdit: true });
                }}
              />
            </Menu>
          </Actions>
        ) : undefined}
      </Stack>
    </>
  );
};

const ConnectedHeader = () => {
  const { t } = useStore();
  return (
    <Stack space={4}>
      <TaktikalHeader isConnected={true} />
      <Text font="regular16" text={t('integrations.taktikal.settings.cosigners.description')} />
      <Separator />
    </Stack>
  );
};

const SignatureLocation = () => {
  const { t } = useStore();
  const signatureLocationQuery = useTaktikalSignatureLocation();
  const signatureLocationMutation = useUpdateTaktikalSignatureLocation();
  const [draft, setDraft] = useState('' as SignatureLocationOptions);
  if (
    signatureLocationQuery.isLoading ||
    signatureLocationQuery.isError ||
    !signatureLocationQuery.data ||
    !signatureLocationQuery.data.signatureLocation
  ) {
    return null;
  }
  const currentLocation = signatureLocationQuery.data?.signatureLocation;

  if (!draft) {
    setDraft(currentLocation);
  }

  const signatureLocationChoices = [
    { value: 'TopFirstPage', label: t('integrations.taktikal.signaturelocation.topfirstpage') },
    { value: 'BottomLastPage', label: t('integrations.taktikal.signaturelocation.bottomlastpage') },
  ];
  const isDirty = draft !== currentLocation;

  return (
    <Card space={4} variant="gray">
      <Stack space={4}>
        <Text font="bold16" text={t('integrations.taktikal.signaturelocation.label')} />
        <Select
          options={signatureLocationChoices}
          defaultValue={draft}
          key={draft}
          isClearable={false}
          onChange={(draft) => setDraft(draft as SignatureLocationOptions)}
        />
        <CancelSaveButtons
          isOpen={isDirty}
          onCancel={() => setDraft(currentLocation)}
          onSave={() => signatureLocationMutation.mutate({ signatureLocation: draft })}
          isDisabled={signatureLocationMutation.isLoading}
        />
      </Stack>
    </Card>
  );
};
const CosignersList = () => {
  const { t } = useStore();
  const { showSuccess } = useToaster();
  const { showModal } = useModal();

  const cosignersQuery = useQuery({
    queryKey: ['taktikal-cosigners'],
    queryFn: () => taktikalGetCosigners(),
  });
  const client = useQueryClient();

  if (cosignersQuery.isError) {
    return null;
  }
  if (cosignersQuery.isSuccess) {
    const cosigners = cosignersQuery.data;
    return (
      <Stack space={4}>
        <Stack space={0} orientation="horizontal" align={'center'} justify={'space-between'}>
          <Text font="bold16" text={t('integrations.taktikal.settings.cosigners')} />
          <Button
            variant="secondary"
            text={t('integrations.taktikal.settings.cosigners.add')}
            iconLeft={<IconAddPlus />}
            onClick={() => showModal('INTEGRATION_TAKTIKAL_COSIGNER', {})}
          />
        </Stack>
        <Stack space={2}>
          {cosigners.length === 0 ? (
            <Card space={6} variant="gray">
              <Stack space={2} orientation="horizontal">
                <IconTeam size={24} />
                <Text font="regular16" text={t('integrations.taktikal.settings.cosigners.none')} />
              </Stack>
            </Card>
          ) : (
            cosigners.map((cosigner) => (
              <Card space={4} key={cosigner.id}>
                <CosignerContainer>
                  <Stack space={1}>
                    <Text font="bold14" text={cosigner.name} />
                    <Text font="regular14" text={cosigner.email} color="purple60" />
                  </Stack>
                  <Stack space={1}>
                    <Text font="regular14" text={cosigner.ssn} color="purple60" />
                    <Text font="regular14" text={cosigner.phone} color="purple60" />
                  </Stack>
                  <Actions>
                    <Menu buttonProps={{ variant: 'ghost', iconLeft: <IconMoreVertical /> }}>
                      <Menu.Item
                        label={t('integrations.taktikal.settings.cosigners.edit')}
                        icon={<IconEdit />}
                        onSelect={() => {
                          showModal('INTEGRATION_TAKTIKAL_COSIGNER', { cosigner });
                        }}
                      />
                      <Menu.Item
                        label={t('integrations.taktikal.settings.cosigners.delete')}
                        icon={<IconRemove />}
                        variant="danger"
                        onSelect={() => {
                          taktikalRemoveCosigner(cosigner.id.toString()).then(() => {
                            showSuccess(
                              t('integrations.taktikal.settings.cosigners.remove.success')
                            );
                            client.resetQueries(['taktikal-cosigners']);
                          });
                        }}
                      />
                    </Menu>
                  </Actions>
                </CosignerContainer>
              </Card>
            ))
          )}
        </Stack>
      </Stack>
    );
  }
  return <Spinner />;
};

export const TaktikalSettingsPage = () => {
  const { t } = useStore();
  const { showModal } = useModal();

  const tokenQuery = useQuery({
    queryKey: ['taktikal-token'],
    queryFn: () => taktikalGetToken(),
  });
  if (tokenQuery.isError) {
    return null;
  }
  if (tokenQuery.isSuccess) {
    if (tokenQuery.data.ok) {
      return (
        <Stack space={4}>
          <ConnectedHeader />
          <SignatureLocation />
          <CosignersList />
        </Stack>
      );
    }
    return (
      <Stack space={4}>
        <TaktikalHeader isConnected={tokenQuery.data.ok} />
        <Text font="regular16" text={t('integrations.taktikal.settings.description')} />
        <Separator />
        <Stack space={2} orientation="horizontal" justify="flex-end">
          <Button
            variant="primary"
            text={t('integrations.taktikal.settings.modal.connect')}
            onClick={() => showModal('INTEGRATION_TAKTIKAL_CONNECT', { isEdit: false })}
          />
        </Stack>
      </Stack>
    );
  }
  return <Spinner />;
};

const CosignerContainer = styled.div({
  display: 'grid',
  gridTemplateColumns: '3fr 2fr 1fr',
});

const Actions = styled.div({
  display: 'flex',
  justifyContent: 'flex-end',
});
